import type { CSSProperties } from 'react';
import type { CoverFont } from './api';

export interface CoverFontOption {
  value: CoverFont;
  label: string;
  family: string;
  titleWeight: number;
  descriptionWeight: number;
  titleTracking: string;
  titleTransform: 'none' | 'uppercase';
}

export const DEFAULT_COVER_FONT: CoverFont = 'montserrat';

export const COVER_FONT_OPTIONS = [
  {
    value: 'montserrat',
    label: 'Montserrat',
    family: '"Montserrat", ui-sans-serif, system-ui, sans-serif',
    titleWeight: 300,
    descriptionWeight: 400,
    titleTracking: '0.08em',
    titleTransform: 'uppercase'
  },
  {
    value: 'raleway',
    label: 'Raleway',
    family: '"Raleway Variable", ui-sans-serif, system-ui, sans-serif',
    titleWeight: 400,
    descriptionWeight: 400,
    titleTracking: '0.075em',
    titleTransform: 'uppercase'
  },
  {
    value: 'lora',
    label: 'Lora',
    family: '"Lora Variable", Georgia, serif',
    titleWeight: 400,
    descriptionWeight: 400,
    titleTracking: '0.01em',
    titleTransform: 'none'
  },
  {
    value: 'playfair-display',
    label: 'Playfair Display',
    family: '"Playfair Display Variable", Georgia, serif',
    titleWeight: 400,
    descriptionWeight: 400,
    titleTracking: '0.01em',
    titleTransform: 'none'
  },
  {
    value: 'cormorant-garamond',
    label: 'Cormorant Garamond',
    family: '"Cormorant Garamond Variable", Georgia, serif',
    titleWeight: 500,
    descriptionWeight: 500,
    titleTracking: '0.015em',
    titleTransform: 'none'
  },
  {
    value: 'cinzel',
    label: 'Cinzel',
    family: '"Cinzel Variable", Georgia, serif',
    titleWeight: 500,
    descriptionWeight: 400,
    titleTracking: '0.045em',
    titleTransform: 'uppercase'
  },
  {
    value: 'caveat',
    label: 'Caveat',
    family: '"Caveat Variable", cursive',
    titleWeight: 500,
    descriptionWeight: 500,
    titleTracking: 'normal',
    titleTransform: 'none'
  },
  {
    value: 'dancing-script',
    label: 'Dancing Script',
    family: '"Dancing Script Variable", cursive',
    titleWeight: 500,
    descriptionWeight: 500,
    titleTracking: 'normal',
    titleTransform: 'none'
  }
] as const satisfies readonly CoverFontOption[];

export function normalizeCoverFont(value: unknown): CoverFont {
  return COVER_FONT_OPTIONS.some((option) => option.value === value)
    ? value as CoverFont
    : DEFAULT_COVER_FONT;
}

export function coverFontOption(value: unknown): CoverFontOption {
  const normalized = normalizeCoverFont(value);
  return COVER_FONT_OPTIONS.find((option) => option.value === normalized) ?? COVER_FONT_OPTIONS[0];
}

export function coverFontVariables(value: unknown): CSSProperties {
  const option = coverFontOption(value);
  return {
    '--cover-font-family': option.family,
    '--cover-title-weight': option.titleWeight,
    '--cover-description-weight': option.descriptionWeight,
    '--cover-title-tracking': option.titleTracking,
    '--cover-title-transform': option.titleTransform
  } as CSSProperties;
}

export function coverFontPreviewStyle(value: unknown): CSSProperties {
  const option = coverFontOption(value);
  return {
    fontFamily: option.family,
    fontWeight: option.titleWeight,
    letterSpacing: option.titleTracking,
    textTransform: option.titleTransform
  };
}
